import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import TiminatorTableSettings, { TiminatorTableColumn } from '../../../components/shared/table-v2/types';
import { Lx } from '../../../i18n/consts';
import { StatusTag } from '../../../components/shared/status-tag';
import { DeleteIconButton } from '../../../components/shared/delete-icon-button';
import { Employee } from '../../../features/employees/types';
import { selectIdOnDelete } from '../../../features/modals';
import { TableTag } from '../../../components/shared/table-tag';
import { normalizeString } from '../../../utils/strings';
import { buildSearchValues, filterByStatusAndSearch, getSortedNamesListValue, getStatusSortValue, getStatusValue } from '../../../utils/table-filters';
import { getVisibleItemsWithRemaining } from '../../../utils/visible-items';
import styles from '../employees.module.scss';
import orderBy from 'lodash/orderBy';

interface UseEmployeesTableOptions {
    search: string;
    selectedStatus: number;
    isMobile: boolean;
    canDelete: boolean;
    onDeleteClick?: (employee: Employee) => void;
}

interface UseEmployeesTableResult extends TiminatorTableSettings<Employee> {
    onDelete: (employee: Employee) => void;
}

const MAX_VISIBLE_TAGS = 2;

type TableTagStyle = React.CSSProperties & {
    '--table-tag-color': string;
    '--table-tag-background': string;
};

const createTableTagStyle = (color: string, background: string): TableTagStyle => ({
    '--table-tag-color': color,
    '--table-tag-background': background,
});

const projectTagStyle = createTableTagStyle('var(--text-accent)', 'var(--background-accent-subdued)');
const roleTagStyle = createTableTagStyle('var(--status-waiting)', 'var(--background-status-waiting-subdued)');

export const useEmployeesTable = ({
    search,
    selectedStatus,
    isMobile,
    canDelete,
    onDeleteClick,
}: UseEmployeesTableOptions): UseEmployeesTableResult => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const employees = useAppSelector(store => store.employees.items);

    const onDelete = React.useCallback((employee: Employee) => {
        if (onDeleteClick) {
            onDeleteClick(employee);
            return;
        }

        dispatch(selectIdOnDelete(employee));
    }, [dispatch, onDeleteClick]);

    const columns = useMemo((): TiminatorTableColumn<Employee>[] => {
        const getFullNameValue = (rowData: Employee) => normalizeString(`${rowData.firstName} ${rowData.lastName}`.trim());

        const statusColumn: TiminatorTableColumn<Employee> = {
            id: 'status',
            label: intl.formatMessage({ id: Lx.General.STATUS }),
            align: isMobile ? 'right' : 'left',
            verticalAlign: isMobile ? 'start' : 'center',
            width: isMobile ? '25%' : '3fr',
            sortable: true,
            getSortValue: (rowData: Employee) => getStatusSortValue(rowData.isActive),
            format: (rowData: Employee) => (
                <StatusTag status={rowData.isActive ? 'active' : 'suspended'}>
                    <span>
                        {rowData.isActive ? (
                            <FormattedMessage id='active' />
                        ) : (
                            <FormattedMessage id='suspended' />
                        )}
                    </span>
                </StatusTag>
            ),
        };

        const deleteColumn: TiminatorTableColumn<Employee> = {
            id: 'deleteButton',
            width: isMobile ? '24px' : '64px',
            align: 'right',
            verticalAlign: isMobile ? 'start' : 'center',
            format: (rowData: Employee) => (
                <DeleteIconButton
                    onClick={() => {
                        onDelete(rowData);
                    }}
                />
            ),
        };

        const renderTagList = (
            items: {id: number; name: string }[],
            isMobileCell: boolean,
            tagStyle: TableTagStyle,
        ) => {
            if (!items.length) {
                return [];
            }

            const sortedItems = orderBy(items, item => (item.name ?? '').toLowerCase(), 'asc');
            const { visibleItems, remainingItems, remainingCount } = getVisibleItemsWithRemaining(sortedItems, MAX_VISIBLE_TAGS);
            const remainingTitle = remainingItems
                .map(item => item.name ?? '')
                .filter(Boolean)
                .join(', ');

            return (
                <div className={isMobileCell ? styles.tagContainerMobile : styles.tagContainer}>
                    {visibleItems.map(item => (
                        <TableTag key={item.id} label={item.name} style={tagStyle} />
                    ))}
                    {remainingCount > 0 ? (
                        <TableTag
                            key='remaining'
                            label={`+${remainingCount}`}
                            title={remainingTitle || `+${remainingCount}`}
                            style={tagStyle}
                        />
                    ) : null}
                </div>
            );
        };

        if (isMobile) {
            return [
                {
                    id: 'employeeDetails',
                    label: intl.formatMessage({ id: Lx.General.NAME }),
                    format: (rowData: Employee) => {
                        const fullName = `${rowData.firstName} ${rowData.lastName}`;
                        const projects = renderTagList(rowData.projects, true, projectTagStyle);
                        const roles = renderTagList(rowData.roles, true, roleTagStyle);
                        const hasTags = rowData.projects.length > 0 || rowData.roles.length > 0;

                        return (
                            <div className={styles.mobileCard}>
                                <div className={styles.mobileCardHeader}>
                                    <div className={styles.mobileCardTitleBlock}>
                                        <div className={styles.mobileInfoRow}><span className={styles.mobileFullName}>{fullName}</span></div>
                                    </div>
                                    <div className={styles.mobileCardActions}>
                                        <StatusTag status={rowData.isActive ? 'active' : 'suspended'}>
                                            <span>
                                                {rowData.isActive ? (
                                                    <FormattedMessage id='active' />
                                                ) : (
                                                    <FormattedMessage id='suspended' />
                                                )}
                                            </span>
                                        </StatusTag>
                                        {canDelete && (
                                            <DeleteIconButton
                                                onClick={() => {
                                                    onDelete(rowData);
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className={styles.mobileInfoRow}><span className={styles.mobileContactValue}>{rowData.email}</span></div>
                                <div className={styles.mobileInfoRow}><span className={styles.mobileContactValue}>{rowData.phoneNumber}</span></div>
                                <div className={styles.mobileInfoRow}><span className={styles.mobilePosition}>{rowData.position}</span></div>
                                {hasTags ? (
                                    <div className={`${styles.mobileInfoRow} ${styles.mobileTagsRow}`}>{projects}{roles}</div>
                                ) : null}
                            </div>
                        );
                    },
                },
            ];
        }

        return [
            {
                id: 'fullName',
                label: intl.formatMessage({ id: Lx.General.NAME }),
                width: '5fr',
                sortable: true,
                getSortValue: getFullNameValue,
                format: (rowData: Employee) => <span className={styles.fullNameWrapText}>{`${rowData.firstName} ${rowData.lastName}`}</span>,
            },
            {
                id: 'email',
                width: '5fr',
                label: intl.formatMessage({ id: Lx.General.EMAIL }),
                sortable: true,
                getSortValue: (rowData: Employee) => normalizeString(rowData.email),
                format: (rowData: Employee) => <span className={styles.desktopContactValue}>{rowData.email}</span>,
            },
            {
                id: 'phone',
                width: '4fr',
                label: intl.formatMessage({ id: Lx.General.PHONENUMBER }),
                sortable: true,
                getSortValue: (rowData: Employee) => normalizeString(rowData.phoneNumber),
                format: (rowData: Employee) =><span className={styles.desktopContactValue}>{rowData.phoneNumber}</span>,
            },
            {
                id: 'position',
                width: '4fr',
                label: intl.formatMessage({ id: Lx.Employee.POSITION }),
                sortable: true,
                getSortValue: (rowData: Employee) => normalizeString(rowData.position),
                format: (rowData: Employee) => <span className={styles.position}>{rowData.position}</span>,
            },
            {
                id: 'projects',
                width: '7fr',
                label: intl.formatMessage({ id: Lx.General.PROJECTS }),
                sortable: true,
                getSortValue: (rowData: Employee) => getSortedNamesListValue(rowData.projects),
                format: (rowData: Employee) => renderTagList(rowData.projects, false, projectTagStyle),
            },
            {
                id: 'roles',
                width: '4fr',
                label: intl.formatMessage({ id: Lx.General.ROLES }),
                sortable: true,
                getSortValue: (rowData: Employee) => getSortedNamesListValue(rowData.roles),
                format: (rowData: Employee) => renderTagList(rowData.roles, false, roleTagStyle),
            },
            statusColumn,
            ...(canDelete ? [deleteColumn] : []),
        ];
    }, [canDelete, intl, isMobile, onDelete]);

    const data = useMemo(() => (
        filterByStatusAndSearch(
            employees,
            selectedStatus,
            employee => employee.isActive,
            search,
            employee => {
                const fullName = normalizeString(`${employee.firstName} ${employee.lastName}`.trim());
                const email = normalizeString(employee.email);
                const phoneNumber = normalizeString(employee.phoneNumber);
                const position = normalizeString(employee.position);
                const projects = getSortedNamesListValue(employee.projects);
                const roles = getSortedNamesListValue(employee.roles);
                const status = normalizeString(getStatusValue(intl, employee.isActive));

                return buildSearchValues([
                    fullName,
                    email,
                    phoneNumber,
                    position,
                    projects,
                    roles,
                    status,
                ]);
            }
        )
    ), [employees, search, selectedStatus, intl]);

    const isEmpty = employees.length === 0;

    return {
        columns,
        data,
        isEmpty,
        onDelete,
    };
};
